import React, { useState, useMemo } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { useNavigate } from "react-router-dom";
import {
  useGetProductsQuery,
  useDeleteProductMutation,
  useGetCategoriesQuery,
} from "state/api";
import {
  PageContainer,
  DataTable,
  TableToolbar,
  StatusChip,
  normalizeList,
  formatCurrency,
} from "components/shared";

// Derive a stock status label + palette key. StatusChip colors itself from
// the shared statusPalette, so we reuse existing keys (active/pending/cancelled)
// and override the visible label to read as stock state.
const stockChip = (product) => {
  const stock = Number(product.stock ?? 0);
  const threshold = Number(product.lowStockThreshold ?? product.minStock ?? 5);
  if (stock <= 0) return <StatusChip status="cancelled" label="Out" />;
  if (stock <= threshold) return <StatusChip status="pending" label="Low" />;
  return <StatusChip status="active" label="In Stock" />;
};

const productImage = (product) => product.images?.[0] || product.image || "";

const Products = () => {
  const navigate = useNavigate();
  const [view, setView] = useState("table");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(0); // 0-based for TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const { data, isFetching } = useGetProductsQuery({
    search,
    page: page + 1,
    limit: rowsPerPage,
  });
  const { data: categoriesData } = useGetCategoriesQuery({});
  const [deleteProduct] = useDeleteProductMutation();

  const { rows: allRows } = useMemo(
    () => normalizeList(data, { page: page + 1, pageSize: rowsPerPage }),
    [data, page, rowsPerPage]
  );

  // The products endpoint (product_routes) returns no grand total — only the
  // current page's `count` plus a `pagination` object of the shape
  // `{ next?: {...}, prev?: {...} }`. normalizeList would otherwise fall back to
  // treating per-page `count` as the total, which strands later pages (Next
  // gets disabled) whenever the catalogue spans more than one page. Derive a
  // real total from the pagination hints instead:
  //   • has `next` → at least one more product exists → use a lower-bound total
  //     so the Next button stays enabled and later pages remain reachable.
  //   • no `next`  → we are on the last (or only) page → exact total = the rows
  //     seen up to and including this page.
  const total = useMemo(() => {
    if (!data) return 0;
    const count = Number(data.count ?? allRows.length ?? 0);
    const seenThroughThisPage = page * rowsPerPage + count; // `page` is 0-based
    return data.pagination?.next ? seenThroughThisPage + 1 : seenThroughThisPage;
  }, [data, page, rowsPerPage, allRows.length]);

  // Category filter is applied client-side on the current page (the products
  // endpoint has no category param).
  const rows = useMemo(
    () =>
      category
        ? allRows.filter(
            (p) => (p.category?.name || p.category) === category
          )
        : allRows,
    [allRows, category]
  );

  const categoryOptions = useMemo(() => {
    const cats = normalizeList(categoriesData).rows;
    return [
      { label: "All Categories", value: "" },
      ...cats.map((c) => ({ label: c.name, value: c.name })),
    ];
  }, [categoriesData]);

  const handleDelete = async (id) => {
    if (window.confirm("Delete this product? This cannot be undone.")) {
      try {
        await deleteProduct(id).unwrap();
      } catch {
        window.alert("Failed to delete product");
      }
    }
  };

  const rowActions = (p) => (
    <>
      <Tooltip title="View / Edit">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/products/add-new?id=${p._id}`);
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton
          size="small"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(p._id);
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  const columns = useMemo(
    () => [
      {
        field: "image",
        headerName: "",
        width: 56,
        render: (p) => (
          <Avatar
            variant="rounded"
            src={productImage(p)}
            alt={p.name}
            sx={{ width: 40, height: 40, bgcolor: "action.selected" }}
          >
            {(p.name || "?").charAt(0).toUpperCase()}
          </Avatar>
        ),
      },
      {
        field: "name",
        headerName: "Name",
        render: (p) => (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {p.name}
            </Typography>
            {(p.productCode || p.code) && (
              <Typography variant="caption" color="text.secondary">
                {p.productCode || p.code}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: "category",
        headerName: "Category",
        render: (p) => p.category?.name || p.category || "—",
      },
      {
        field: "price",
        headerName: "Price",
        align: "right",
        render: (p) => formatCurrency(p.sellingPrice ?? p.price),
      },
      {
        field: "stock",
        headerName: "Stock",
        render: (p) => stockChip(p),
      },
      {
        field: "actions",
        headerName: "",
        align: "right",
        render: (p) => rowActions(p),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate]
  );

  return (
    <PageContainer
      title="Products"
      subtitle="Browse, search and manage your product catalogue"
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/products/add-new")}
        >
          Add Product
        </Button>
      }
    >
      <TableToolbar
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        searchPlaceholder="Search products by name or code…"
        filters={[
          {
            label: "Category",
            value: category,
            onChange: setCategory,
            options: categoryOptions,
          },
        ]}
        actions={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, v) => v && setView(v)}
          >
            <ToggleButton value="table" aria-label="table view">
              <ViewListIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="cards" aria-label="cards view">
              <ViewModuleIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        }
      />

      {view === "table" ? (
        <DataTable
          columns={columns}
          rows={rows}
          loading={isFetching}
          emptyText="No products found"
          onRowClick={(p) => navigate(`/products/add-new?id=${p._id}`)}
          page={page}
          rowsPerPage={rowsPerPage}
          total={total}
          onPageChange={setPage}
          onRowsPerPageChange={(n) => {
            setRowsPerPage(n);
            setPage(0);
          }}
        />
      ) : (
        <Grid container spacing={2}>
          {rows.length === 0 && !isFetching && (
            <Grid item xs={12}>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ py: 6 }}
              >
                No products found
              </Typography>
            </Grid>
          )}
          {rows.map((p) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={p._id}>
              <Card
                variant="outlined"
                sx={{ height: "100%", display: "flex", flexDirection: "column" }}
              >
                <Box
                  sx={{
                    height: 160,
                    bgcolor: "action.hover",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {productImage(p) ? (
                    <Box
                      component="img"
                      src={productImage(p)}
                      alt={p.name}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Typography variant="h4" color="text.secondary">
                      {(p.name || "?").charAt(0).toUpperCase()}
                    </Typography>
                  )}
                </Box>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" noWrap title={p.name}>
                    {p.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.category?.name || p.category || "—"}
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body1" fontWeight={700}>
                      {formatCurrency(p.sellingPrice ?? p.price)}
                    </Typography>
                    {stockChip(p)}
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
                  {rowActions(p)}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </PageContainer>
  );
};

export default Products;
